/** One knowledge article, opened as a side drawer over whatever you were
 *  reading: title, tags, the structured body, and the tick that marks it read.
 *  A drawer rather than a page so your place in the list is never lost. */
import { useEffect } from 'react';
import type { Article } from '@/data/types';
import { ArticleBody } from './ArticleBody';

interface Props {
  article: Article;
  completed: Set<string>;
  onToggleRead: (id: string) => void;
  onClose: () => void;
}

export function ArticleDrawer({ article, completed, onToggleRead, onClose }: Props) {
  const isDone = completed.has(article.id);
  const tags = article.tags || [];
  const title = typeof article.title === 'string' ? article.title : article.title.en;
  const sub = typeof article.sub === 'string' ? article.sub : article.sub?.en;

  // Lock body scroll while open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // ESC to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer" role="dialog" aria-modal="true">
        <div className="drawer-head">
          <div className="h-meta">
            <div className="h-tags">
              {tags.map((t, i) => (
                <span key={i} className={`tag ${t.cls || ''}`}>
                  {t.label}
                </span>
              ))}
            </div>
            <h1>{title}</h1>
            <p className="sub">{sub}</p>
          </div>
          <button className="drawer-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="drawer-body">
          <ArticleBody blocks={article.body} />
        </div>
        <div className="drawer-foot">
          <button
            className={isDone ? 'btn btn-ghost' : 'btn btn-success'}
            onClick={() => onToggleRead(article.id)}
          >
            {isDone ? '↶ Mark as unread' : '✓ Mark as read'}
          </button>
          <div className="spacer" />
          <button className="btn btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </>
  );
}
